import { KeyboardEvent, MouseEvent } from 'react';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { Path } from 'react-router-dom';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
  Avatar,
  Button,
  Icon,
  SocialMediaIcon,
  SocialMediaIconProps,
} from '@kontentino/ui';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faCircleExclamation';
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons/faCircleXmark';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faComment } from '@fortawesome/pro-regular-svg-icons/faComment';
import { faLinkSlash } from '@fortawesome/pro-regular-svg-icons/faLinkSlash';
import { faListCheck } from '@fortawesome/pro-regular-svg-icons/faListCheck';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan';
import Link from 'components/shared/Link';
import routes, { modalRoutes } from 'constants/routes';
import PageTypeUtils from 'app/utils/pageType';
import { formatDate, formatDateTime } from 'app/utils/date';
import { openUrl, toRouterPath } from 'utils/url';
import useBoolean from 'utils/hooks/useBoolean';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';
import { resolvePostModalHashForPreference } from 'app/modules/posts/utils/postModalDeepLinkHash';
import PostsConfig from 'app/config/posts';
import {
  InAppNotification,
  InAppNotificationActor,
  InAppNotificationPage,
  InAppNotificationPost,
  PageConnectionNotification,
} from 'types/InAppNotification';
import { useMarkNotificationAsRead } from 'app/modules/inAppNotifications/hooks/useInAppNotificationActions';

const NOTIFICATION_TYPE = {
  PostComment: 1,
  PostTaskAssigned: 2,
  PostToApprove: 3,
  PostTaskDone: 4,
  PostApproved: 5,
  PostRejected: 6,
  PostCommentMention: 7,
  GlobalPostUpdated: 12,
  GlobalPostDeleted: 13,
  PostFailedToPublish: 130,
  PageConnectionRevoked: 131,
  PageConnectionPostsAtRisk: 132,
  PageSingleTokenRecommendation: 133,
  PageConnectionExpiringSoon: 134,
  PageConnectionPermissionDowngraded: 135,
} as const;

const COLLAPSED_GROUP_CHILDREN_LIMIT = 5;

const PAGE_CONNECTION_COPY: Record<
  PageConnectionNotification['type'],
  { icon: IconDefinition; singleKey: string; groupKey: string }
> = {
  [NOTIFICATION_TYPE.PageConnectionRevoked]: {
    icon: faLinkSlash,
    singleKey: 'notifications.row.lostConnection',
    groupKey: 'notifications.row.profilesDisconnected',
  },
  [NOTIFICATION_TYPE.PageConnectionPostsAtRisk]: {
    icon: faLinkSlash,
    singleKey: 'notifications.row.lostConnection',
    groupKey: 'notifications.row.profilesDisconnected',
  },
  [NOTIFICATION_TYPE.PageConnectionPermissionDowngraded]: {
    icon: faLinkSlash,
    singleKey: 'notifications.row.permissionLost',
    groupKey: 'notifications.row.permissionLostProfiles',
  },
  [NOTIFICATION_TYPE.PageConnectionExpiringSoon]: {
    icon: faCircleExclamation,
    singleKey: 'notifications.row.expiringSoon',
    groupKey: 'notifications.row.expiringSoonProfiles',
  },
  [NOTIFICATION_TYPE.PageSingleTokenRecommendation]: {
    icon: faCircleExclamation,
    singleKey: 'notifications.row.singleToken',
    groupKey: 'notifications.row.singleTokenProfiles',
  },
};

const MAX_NAMED = 3;
const MAX_AVATARS = 3;

const AVATAR_STACKS: Record<number, { size: number; positions: string[] }> = {
  1: { size: 40, positions: ['tw-left-0 tw-top-0'] },
  2: {
    size: 24,
    positions: ['tw-left-[2px] tw-top-[2px]', 'tw-bottom-[2px] tw-right-[2px]'],
  },
  3: {
    size: 20,
    positions: [
      'tw-left-[2px] tw-top-[12px]',
      'tw-left-[14px] tw-top-[2px]',
      'tw-bottom-[2px] tw-right-[2px]',
    ],
  },
};

const META_PROVIDER_NETWORKS = ['facebook', 'instagram', 'threads'];

type RowTitle = {
  key: string;
  values?: Record<string, string | number | null | undefined>;
};

type NotificationContent = {
  icon: IconDefinition;
  title: RowTitle;
  meta: string;
};

function rowTitle(key: string, values?: RowTitle['values']): RowTitle {
  return { key, values };
}

function summarizeNames(names: (string | null)[]): string {
  const unique = Array.from(new Set(names.filter((name) => !!name)));

  return unique.length > MAX_NAMED
    ? `${unique.slice(0, MAX_NAMED).join(', ')} +${unique.length - MAX_NAMED}`
    : unique.join(', ');
}

function joinMeta(...parts: (string | null | undefined | false)[]): string {
  return parts.filter(Boolean).join(' · ');
}

function quote(message: string | null): string | null {
  return message ? `“${message}”` : null;
}

function networkLabel(socialNetwork: string): string {
  const name = PageTypeUtils.getIconNameFromName(socialNetwork);

  return name
    ? PageTypeUtils.getLabel(PageTypeUtils.getNumberFromName(name))
    : socialNetwork;
}

function socialMediaIconType(
  socialNetwork: string,
): SocialMediaIconProps['type'] | null {
  const name = PageTypeUtils.getIconNameFromName(socialNetwork);

  return name ? (name as SocialMediaIconProps['type']) : null;
}

function causeLabel(page: InAppNotificationPage, t: TFunction): string | null {
  const provider = META_PROVIDER_NETWORKS.includes(page.socialNetwork)
    ? 'Meta'
    : networkLabel(page.socialNetwork);

  switch (page.cause) {
    case 'revoked':
      return t('notifications.cause.revoked', { provider });
    case 'page_removed':
      return t('notifications.cause.pageRemoved', { provider });
    case 'page_permission':
    case 'permission_downgraded':
      return t('notifications.cause.permission', { provider });
    case 'expired':
      return t('notifications.cause.expired');
    default:
      return null;
  }
}

function postsAtRiskMeta(page: InAppNotificationPage, t: TFunction) {
  if (!page.postsAtRisk) {
    return null;
  }

  return page.nextPostAt
    ? t('notifications.row.postsScheduled', {
        count: page.postsAtRisk,
        when: dayjs(page.nextPostAt).fromNow(),
      })
    : t('notifications.row.postsAtRisk', { count: page.postsAtRisk });
}

function scheduledFor(post: InAppNotificationPost | undefined): string | null {
  if (!post?.date) {
    return null;
  }

  if (!post.time) {
    const scheduledOn = dayjs(post.date);

    return scheduledOn.isValid() ? formatDate(scheduledOn) : null;
  }

  const scheduledAt = dayjs(`${post.date} ${post.time}`);

  return scheduledAt.isValid() ? formatDateTime(scheduledAt) : null;
}

function singlePageMeta(
  type: PageConnectionNotification['type'],
  page: InAppNotificationPage,
  t: TFunction,
): string {
  const network = networkLabel(page.socialNetwork);

  switch (type) {
    case NOTIFICATION_TYPE.PageConnectionRevoked:
    case NOTIFICATION_TYPE.PageConnectionPostsAtRisk:
      return joinMeta(network, postsAtRiskMeta(page, t), causeLabel(page, t));
    case NOTIFICATION_TYPE.PageConnectionPermissionDowngraded:
      return joinMeta(network, postsAtRiskMeta(page, t));
    case NOTIFICATION_TYPE.PageConnectionExpiringSoon:
      return joinMeta(
        network,
        page.expiresAt &&
          t('notifications.row.expiresOn', {
            date: formatDateTime(page.expiresAt),
          }),
      );
    case NOTIFICATION_TYPE.PageSingleTokenRecommendation:
      return t('notifications.row.addBackupConnection');
  }
}

function pageGroupMeta(
  type: PageConnectionNotification['type'],
  pages: InAppNotificationPage[],
  t: TFunction,
): string {
  const names = summarizeNames(pages.map(({ name }) => name));

  switch (type) {
    case NOTIFICATION_TYPE.PageConnectionExpiringSoon:
      return names;
    case NOTIFICATION_TYPE.PageSingleTokenRecommendation:
      return t('notifications.row.addBackupConnection');
    default: {
      const causes = pages.map((page) => causeLabel(page, t));
      const sharedCause = causes.every((cause) => cause === causes[0])
        ? causes[0]
        : null;
      const postsAtRisk = pages.reduce(
        (sum, page) => sum + (page.postsAtRisk ?? 0),
        0,
      );

      return joinMeta(
        sharedCause ?? names,
        postsAtRisk > 0 &&
          t('notifications.row.postsAtRisk', { count: postsAtRisk }),
      );
    }
  }
}

function pageConnectionContent(
  { type, pages }: PageConnectionNotification,
  t: TFunction,
): NotificationContent {
  const { icon, singleKey, groupKey } = PAGE_CONNECTION_COPY[type];

  return pages.length > 1
    ? {
        icon,
        title: rowTitle(groupKey, { count: pages.length }),
        meta: pageGroupMeta(type, pages, t),
      }
    : {
        icon,
        title: rowTitle(singleKey, { name: pages[0].name }),
        meta: singlePageMeta(type, pages[0], t),
      };
}

export function getNotificationContent(
  notification: InAppNotification,
  t: TFunction,
): NotificationContent {
  if ('pages' in notification) {
    return pageConnectionContent(notification, t);
  }

  const name = notification.actors[0]?.name ?? notification.author ?? '';
  const postCount = Math.max(notification.posts.length, 1);
  const pagesMeta = summarizeNames(
    notification.posts.map((post) => post.page_name),
  );

  switch (notification.type) {
    case NOTIFICATION_TYPE.PostComment:
    case NOTIFICATION_TYPE.PostCommentMention:
      return notification.event_count > 1
        ? {
            icon: faComment,
            title: rowTitle('notifications.row.comments', {
              count: notification.event_count,
              page: notification.page_name,
            }),
            meta: summarizeNames(notification.actors.map((a) => a.name)),
          }
        : {
            icon: faComment,
            title: rowTitle(
              notification.type === NOTIFICATION_TYPE.PostCommentMention
                ? 'notifications.row.mention'
                : 'notifications.row.comment',
              { name },
            ),
            meta: joinMeta(pagesMeta, quote(notification.message)),
          };
    case NOTIFICATION_TYPE.PostToApprove:
      return {
        icon: faClock,
        title: rowTitle('notifications.row.approvalRequest', {
          count: postCount,
        }),
        meta: pagesMeta,
      };
    case NOTIFICATION_TYPE.PostApproved:
      return {
        icon: faCircleCheck,
        title: rowTitle('notifications.row.approved', {
          name,
          count: postCount,
        }),
        meta: pagesMeta,
      };
    case NOTIFICATION_TYPE.PostRejected:
      return {
        icon: faCircleXmark,
        title: rowTitle('notifications.row.rejected', {
          name,
          count: postCount,
        }),
        meta: pagesMeta,
      };
    case NOTIFICATION_TYPE.PostTaskAssigned:
      return {
        icon: faListCheck,
        title: rowTitle('notifications.row.taskAssigned', {
          name,
          count: postCount,
        }),
        meta: pagesMeta,
      };
    case NOTIFICATION_TYPE.PostTaskDone:
      return {
        icon: faCircleCheck,
        title: rowTitle('notifications.row.taskDone', {
          name,
          count: postCount,
        }),
        meta: pagesMeta,
      };
    case NOTIFICATION_TYPE.GlobalPostUpdated:
      return {
        icon: faArrowsRotate,
        title: rowTitle('notifications.row.globalPostUpdated'),
        meta: t('notifications.row.pendingChanges'),
      };
    case NOTIFICATION_TYPE.GlobalPostDeleted:
      return {
        icon: faTrashCan,
        title: rowTitle('notifications.row.globalPostDeleted'),
        meta: pagesMeta,
      };
    case NOTIFICATION_TYPE.PostFailedToPublish: {
      if (notification.posts.length > 1) {
        return {
          icon: faCircleExclamation,
          title: rowTitle('notifications.row.postsFailed', {
            count: notification.posts.length,
          }),
          meta: pagesMeta,
        };
      }

      const [post] = notification.posts;
      const when = scheduledFor(post);

      return {
        icon: faCircleExclamation,
        title: rowTitle('notifications.row.postFailed', {
          page: post?.page_name ?? notification.page_name,
          network: post?.page_type
            ? PageTypeUtils.getLabel(post.page_type)
            : '',
        }),
        meta: joinMeta(
          when && t('notifications.row.scheduledFor', { when }),
          t(
            notification.is_read
              ? 'notifications.row.reviewed'
              : 'notifications.row.notYetReviewed',
          ),
        ),
      };
    }
  }
}

function rowActors(notification: InAppNotification): InAppNotificationActor[] {
  if (notification.category !== 'collaboration') {
    return [];
  }

  const actors =
    notification.actors.length > 0
      ? notification.actors
      : [{ name: notification.author, avatarSrc: notification.picture }];
  const seen = new Set<string>();

  return actors
    .filter(({ name, avatarSrc }) => {
      const key = name ?? avatarSrc;

      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    })
    .slice(0, MAX_AVATARS);
}

function ActorAvatars({ actors }: { actors: InAppNotificationActor[] }) {
  const { size, positions } = AVATAR_STACKS[actors.length];

  return (
    <span className="tw-relative tw-h-10 tw-w-10 tw-shrink-0">
      {actors.map((actor, index) => (
        <Avatar
          key={actor.name ?? actor.avatarSrc}
          size={size}
          name={actor.name ?? undefined}
          src={actor.avatarSrc ?? undefined}
          className={clsx('tw-absolute', positions[index])}
          data-testid="notification-row_avatar"
        />
      ))}
    </span>
  );
}

function formatNotificationTime(createdAt: string): string {
  const date = dayjs.utc(createdAt).local();

  return date.isSame(dayjs(), 'day') ? date.fromNow() : formatDateTime(date);
}

function postModalHash(isNewPostExperienceActive: boolean): string {
  return `#${
    isNewPostExperienceActive
      ? modalRoutes.POST_DETAIL_NEW
      : modalRoutes.POST_DETAIL
  }`;
}

function notificationPath(
  notification: InAppNotification,
  isNewPostExperienceActive: boolean,
): Partial<Path> | null {
  const path = notification.link ? toRouterPath(notification.link) : null;

  if (!path) {
    return null;
  }

  const search = new URLSearchParams(path.search);

  if (notification.comment_type) {
    search.set(
      PostsConfig.COMMUNICATION_TAB_QUERY_PARAM_KEY,
      notification.comment_type,
    );
  }

  return {
    pathname: path.pathname,
    search: search.toString() ? `?${search}` : '',
    hash:
      resolvePostModalHashForPreference(
        path.hash ?? '',
        isNewPostExperienceActive,
      ) ?? path.hash,
  };
}

type RowChild = {
  key: string;
  to: string | Partial<Path>;
  iconType: SocialMediaIconProps['type'] | null;
  label: string;
};

function rowChildren(
  notification: InAppNotification,
  isNewPostExperienceActive: boolean,
): RowChild[] {
  if ('pages' in notification) {
    return notification.pages.map((page) => ({
      key: `${page.socialNetwork}-${page.externalPageId ?? page.name}`,
      to: routes.SETTINGS.AUTHORIZATION,
      iconType: socialMediaIconType(page.socialNetwork),
      label: page.name,
    }));
  }

  if (notification.type === NOTIFICATION_TYPE.GlobalPostDeleted) {
    return [];
  }

  return notification.posts.flatMap((post) =>
    post.id
      ? [
          {
            key: String(post.id),
            to: {
              pathname: routes.POSTS_CALENDAR,
              search: `?id=${post.id}`,
              hash: postModalHash(isNewPostExperienceActive),
            },
            iconType: post.page_type
              ? socialMediaIconType(PageTypeUtils.getName(post.page_type))
              : null,
            label: joinMeta(post.page_name, scheduledFor(post)),
          },
        ]
      : [],
  );
}

function isNewTabClick(event: MouseEvent) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0;
}

type Props = {
  notification: InAppNotification;
};

function NotificationRow({ notification }: Props) {
  const { t } = useTranslation();
  const isNewPostExperienceActive = usePostsLayoutState(
    (state) => state.isNewPostExperienceActive,
  );
  const { mutate: markAsRead, mutateAsync: markAsReadAsync } =
    useMarkNotificationAsRead();
  const [isExpanded, setIsExpanded] = useBoolean(false);
  const [isShowingAllChildren, setIsShowingAllChildren] = useBoolean(false);

  const content = getNotificationContent(notification, t);
  const actors = rowActors(notification);
  const children = rowChildren(notification, isNewPostExperienceActive);
  const isGroup = children.length > 1;
  const visibleChildren = isShowingAllChildren
    ? children
    : children.slice(0, COLLAPSED_GROUP_CHILDREN_LIMIT);
  const hiddenChildrenCount = children.length - visibleChildren.length;
  const isUnread = !notification.is_read;
  const isCritical = notification.severity === 'critical';
  const singleTarget =
    'pages' in notification
      ? children[0]?.to
      : notificationPath(notification, isNewPostExperienceActive);
  const hasTarget = isGroup || !!singleTarget || !!notification.link;
  const hasNamedTitle = content.title.values?.name !== undefined;

  const markReadIfUnread = () => {
    if (isUnread) {
      markAsRead(notification);
    }
  };

  const handleGroupToggle = () => {
    if (isExpanded) {
      markReadIfUnread();
    }
    setIsExpanded.toggle();
  };

  const onActivationKey =
    (action: () => void) => (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        action();
      }
    };

  const handleExternalLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isUnread || isNewTabClick(event)) {
      markReadIfUnread();
      return;
    }

    event.preventDefault();
    const { href } = event.currentTarget;
    markAsReadAsync(notification)
      .catch(() => undefined)
      .finally(() => openUrl(href));
  };

  const rowClassName = clsx(
    'tw-flex tw-w-full tw-items-center tw-gap-3 tw-p-3 tw-text-left tw-outline-none',
    { 'tw-cursor-pointer': hasTarget, 'tw-cursor-default': !hasTarget },
  );

  const rowContent = (
    <>
      {actors.length > 0 ? (
        <ActorAvatars actors={actors} />
      ) : (
        <span
          className={clsx(
            'tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center',
            {
              'tw-text-danger-140': isCritical,
              'tw-text-grayscale-180': !isCritical && isUnread,
              'tw-text-grayscale-90': !isCritical && !isUnread,
            },
          )}
          data-testid="notification-row_icon"
        >
          <Icon icon={content.icon} size="md" />
        </span>
      )}
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
        <span
          className={clsx('tw-text-md', {
            'tw-flex tw-min-w-0 tw-overflow-hidden tw-whitespace-pre':
              hasNamedTitle,
            'tw-truncate': !hasNamedTitle,
            'tw-font-semibold': isUnread,
            'tw-font-medium': !isUnread,
            'tw-text-danger-140': isCritical,
            'tw-text-grayscale-180': !isCritical && isUnread,
            'tw-text-grayscale-100': !isCritical && !isUnread,
          })}
        >
          <Trans
            i18nKey={content.title.key}
            values={content.title.values}
            components={{
              name: <span className="tw-min-w-0 tw-truncate" />,
            }}
          />
        </span>
        {content.meta && (
          <span
            className={clsx('tw-truncate tw-text-sm', {
              'tw-text-grayscale-140': isUnread,
              'tw-text-grayscale-90': !isUnread,
            })}
          >
            {content.meta}
          </span>
        )}
        <span
          className={clsx('tw-text-sm', {
            'tw-text-danger-100': isCritical,
            'tw-text-grayscale-90': !isCritical,
          })}
        >
          {formatNotificationTime(notification.created_at)}
        </span>
      </div>
      {isUnread && (
        <span
          className="tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-primary-100"
          data-testid="notification-row_unread-dot"
        />
      )}
      {hasTarget && (
        <span
          className={clsx('tw-flex tw-shrink-0 tw-transition-transform', {
            'tw-rotate-180': isExpanded,
            'tw-text-grayscale-180': isUnread,
            'tw-text-grayscale-90': !isUnread,
          })}
          data-testid="notification-row_chevron"
        >
          <Icon icon={isGroup ? faChevronDown : faChevronRight} size="sm" />
        </span>
      )}
    </>
  );

  const renderRow = () => {
    if (isGroup) {
      return (
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          className={rowClassName}
          onClick={handleGroupToggle}
          onKeyDown={onActivationKey(handleGroupToggle)}
        >
          {rowContent}
        </div>
      );
    }

    if (singleTarget) {
      return (
        <Link
          to={singleTarget}
          isRouterLink
          className={rowClassName}
          onClick={markReadIfUnread}
          onAuxClick={markReadIfUnread}
        >
          {rowContent}
        </Link>
      );
    }

    if (notification.link) {
      return (
        <a
          href={notification.link}
          className={rowClassName}
          onClick={handleExternalLinkClick}
          onAuxClick={markReadIfUnread}
        >
          {rowContent}
        </a>
      );
    }

    if (!isUnread) {
      return <div className={rowClassName}>{rowContent}</div>;
    }

    return (
      <div
        role="button"
        tabIndex={0}
        className={rowClassName}
        onClick={markReadIfUnread}
        onKeyDown={onActivationKey(markReadIfUnread)}
      >
        {rowContent}
      </div>
    );
  };

  return (
    <div
      className={clsx('tw-rounded-lg tw-border tw-transition-colors', {
        'tw-border-danger-20 tw-bg-danger-10': isCritical,
        'tw-border-grayscale-10 tw-bg-white': !isCritical,
        'hover:tw-border-danger-100': isCritical && hasTarget,
        'hover:tw-border-primary-100': !isCritical && hasTarget,
      })}
      data-cy="navbar_notifications_item"
      data-testid="notification-row"
      data-is-unread={isUnread}
      data-severity={notification.severity}
    >
      {renderRow()}
      {isGroup && isExpanded && (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-px-3 tw-pb-3">
          {visibleChildren.map((child) => (
            <Link
              key={child.key}
              to={child.to}
              isRouterLink
              onClick={markReadIfUnread}
              onAuxClick={markReadIfUnread}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded tw-bg-grayscale-5 tw-px-3 tw-py-2 tw-text-md tw-text-grayscale-180 hover:tw-bg-primary-10"
              data-cy="navbar_notifications_item-child"
            >
              {child.iconType && (
                <SocialMediaIcon type={child.iconType} size={16} />
              )}
              <span className="tw-flex-1 tw-truncate">{child.label}</span>
              <span className="tw-flex tw-text-grayscale-90">
                <Icon icon={faChevronRight} size="sm" />
              </span>
            </Link>
          ))}
          {hiddenChildrenCount > 0 && (
            <Button
              variant="plain"
              size="small"
              className="tw-self-start"
              onClick={setIsShowingAllChildren.on}
              data-cy="navbar_notifications_item-show-more"
            >
              {t('notifications.row.showMore', { count: hiddenChildrenCount })}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationRow;
